import { Button } from "@/components/_components/button";
import { Card, CardContent } from "@/components/_components/card";
import { Skeleton } from "@/components/_components/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/_components/dialog";
import { useGetConfigDetails } from "@/lib/api/hooks";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import { KbConfigResponse, KbInfoResponse } from "@/lib/api/types";
import { AGENT_TYPE, PAGINATION } from "@/constants/constants";
import { Search, RotateCcw, Edit, Plus, FolderOpen, BarChart3, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Database } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { cn, formatDateTime } from "@/lib/utils";
import { getFileManagementRoute } from "@/constants/routes";
import { usePageHeader } from "@/contexts/page-header-context";
import { Badge } from "@/components/_components/badge";
import { DynamicKbForm } from "./_components/DynamicKbForm";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuthorities } from "@/hooks/useAuthorities";

// Input and textarea styles
const inputStyles =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors " +
  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground " +
  "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

const textareaStyles =
  "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm " +
  "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

// Agent types from constants
const AGENT_TYPES = (AGENT_TYPE as readonly string[]).map(type => ({
  id: type,
  name: type === "CANDIDATE_OUTREACH" ? "Candidate Outreach" :
    type === "TPMO" ? "TPMO" :
      type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
}));

interface KbInfoFilter {
  agentType: string;
  kbType: string;
}

interface EditKbInfoData {
  id?: number | null;
  agentType: string;
  kbId: string;
  bucketName: string;
  kbType: string;
  kbTypeValue: string;
  kbTypeId: string;
  dataSourceId: string;
  metadataSchema: string;
  dynamicFields: Record<string, string>;
  dynamicFieldIds: Record<string, string>;
}

const KnowledgeBaseSetupPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_KNOWLEDGE_MGMT_VIEW">
      <KnowledgeBaseSetupContent />
    </PageAccessGuard>
  );
};

const KnowledgeBaseSetupContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const { hasAuthority } = useAuthorities();
  const [formData, setFormData] = useState({
    agentType: searchParams.get("agentType") || "",
    kbType: searchParams.get("kbType") || "",
  });
  const [kbTypes, setKbTypes] = useState<KbConfigResponse[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingKbInfo, setEditingKbInfo] = useState<EditKbInfoData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const currentFilterRef = useRef<KbInfoFilter | null>(null);

  // Query for KB config - only run when agent type is selected
  const {
    data: kbConfigResponse,
    isLoading: isLoadingConfig,
  } = useGetConfigDetails("kbInfo", formData.agentType, {
    query: {
      enabled: !!formData.agentType,
    },
  });

  // Use paginated query hook for KB info
  const paginatedQuery = usePaginatedQuery<KbInfoFilter, KbInfoResponse[]>(
    (filter, options) => api.getKbInfo(filter, options),
    {
      pageSize: pageSize,
    }
  );

  const {
    data: kbInfoData,
    isPending: isLoading,
    error,
    executeQuery,
    currentPage,
    totalCount: total,
    setCurrentPage,
  } = paginatedQuery;

  // Handle KB config response
  useEffect(() => {
    if (kbConfigResponse?.data) {
      const data = kbConfigResponse.data;
      setKbTypes(data);
      // Auto-select if only one option
      if (data && data.length === 1) {
        setFormData(prev => ({ ...prev, kbType: data[0].kbType }));
      } else if (data && data.length > 1) {
        // Reset kbType if multiple options are available and current selection is not valid
        const isValidKbType = data.some(kb => kb.kbType === formData.kbType);
        if (!isValidKbType) {
          setFormData(prev => ({ ...prev, kbType: "" }));
        }
      }
    }
  }, [kbConfigResponse?.data, formData.kbType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset kbType and clear results when agentType changes
    if (field === "agentType") {
      setFormData(prev => ({ ...prev, kbType: "" }));
      setHasSearched(false);
      // Clear the current filter to remove table results
      paginatedQuery.setCurrentFilter(null);
    }

    // Clear results when kbType changes
    if (field === "kbType") {
      setHasSearched(false);
      // Clear the current filter to remove table results
      paginatedQuery.setCurrentFilter(null);
    }
  };

  const handleSearch = useCallback(() => {
    if (!formData.agentType) {
      toast.error("Please select an Agent Type");
      return;
    }
    if (!formData.kbType) {
      toast.error("Please select a KB Type");
      return;
    }

    const filter: KbInfoFilter = {
      agentType: formData.agentType,
      kbType: formData.kbType,
    };

    currentFilterRef.current = filter;
    executeQuery(filter, 1); // Reset to first page on new search
    setHasSearched(true);
    setCurrentPage(1);

    // Update URL params
    const newSearchParams = new URLSearchParams();
    newSearchParams.set("agentType", formData.agentType);
    newSearchParams.set("kbType", formData.kbType);
    setSearchParams(newSearchParams);

  }, [formData.agentType, formData.kbType, executeQuery, setSearchParams, setCurrentPage]);

  const handleReset = () => {
    setFormData({
      agentType: "",
      kbType: "",
    });
    setKbTypes([]);
    setHasSearched(false);
    currentFilterRef.current = null;
    setCurrentPage(1);

    // Clear URL params
    const newSearchParams = new URLSearchParams();
    setSearchParams(newSearchParams);

    toast.success("Form reset successfully");
  };

  const handleEdit = (kbInfo: KbInfoResponse) => {
    setIsCreateMode(false);

    // Parse existing metadata to populate dynamic fields
    let dynamicFields: Record<string, string> = {};
    let dynamicFieldIds: Record<string, string> = {};

    try {
      if (kbInfo.metadataSchema) {
        const metadata = JSON.parse(kbInfo.metadataSchema);
        dynamicFields = metadata;
      }
    } catch (error) {
      console.error('Error parsing metadata schema:', error);
    }

    // Find the KB type config to get the main field key
    const kbTypeConfig = kbTypes.find(kb => kb.kbType === kbInfo.kbType);
    
    // Add the kbTypeValue to dynamicFields using the main field key
    if (kbTypeConfig?.key && kbInfo.kbTypeValue) {
      dynamicFields[kbTypeConfig.key] = kbInfo.kbTypeValue;
    }

    setEditingKbInfo({
      id: kbInfo.id,
      agentType: kbInfo.agentType,
      kbId: kbInfo.kbId,
      bucketName: kbInfo.bucketName || "",
      kbType: kbInfo.kbType,
      kbTypeValue: kbInfo.kbTypeValue || "",
      kbTypeId: "",
      dataSourceId: kbInfo.dataSourceId || "",
      metadataSchema: kbInfo.metadataSchema || "",
      dynamicFields,
      dynamicFieldIds
    });
    setIsEditDialogOpen(true);
  };

  const handleCreate = () => {
    setIsCreateMode(true);
    setEditingKbInfo({
      id: null,
      agentType: formData.agentType,
      kbId: "",
      bucketName: "",
      kbType: formData.kbType,
      kbTypeValue: "",
      kbTypeId: "",
      dataSourceId: "",
      metadataSchema: "",
      dynamicFields: {},
      dynamicFieldIds: {}
    });
    setIsEditDialogOpen(true);
  };

  const handleEditInputChange = (field: keyof EditKbInfoData, value: string) => {
    if (editingKbInfo) {
      setEditingKbInfo(prev => ({
        ...prev!,
        [field]: value
      }));
    }
  };

  const handleDynamicFieldChange = (key: string, value: string, kbTypeId?: string) => {
    if (editingKbInfo) {
      // Use editingKbInfo.kbType instead of formData.kbType for the edit dialog
      const editingKbTypeConfig = kbTypes.find(kb => kb.kbType === editingKbInfo.kbType);

      setEditingKbInfo(prev => {
        const updated = {
          ...prev!,
          dynamicFields: {
            ...prev!.dynamicFields,
            [key]: value
          },
          dynamicFieldIds: {
            ...prev!.dynamicFieldIds,
            [key]: kbTypeId || ''
          },
          // Update kbTypeValue and kbTypeId if this is the main field
          ...(editingKbTypeConfig?.key === key ? {
            kbTypeValue: value,
            kbTypeId: kbTypeId || ''
          } : {})
        };
        return updated;
      });
    }
  };

  // Function to extract keys from folder structure recursively
  const extractKeysFromFolders = (folders: any): string[] => {
    const keys: string[] = [];

    if (!folders) {
      return keys;
    }

    // Handle array of folders
    if (Array.isArray(folders)) {
      folders.forEach((folder) => {
        if (folder.key) {
          keys.push(folder.key);
        }
        if (folder.folders) {
          keys.push(...extractKeysFromFolders(folder.folders));
        }
      });
    } else {
      // Handle single folder object
      // Add the current folder's key if it exists
      if (folders.key) {
        keys.push(folders.key);
      }

      // Recursively process nested folders
      if (folders.folders) {
        const nestedKeys = extractKeysFromFolders(folders.folders);
        keys.push(...nestedKeys);
      }
    }

    return keys;
  };

  // Function to generate metadata based on dynamic fields and KB configuration
  const generateMetadataFromDynamicFields = (): string => {
    if (!editingKbInfo) return '';

    const selectedKbTypeConfig = kbTypes.find(kb => kb.kbType === editingKbInfo.kbType);
    if (!selectedKbTypeConfig) return '';

    // Create the metadata object structure - flat object without metadataAttributes wrapper
    const metadataObject: { [key: string]: string } = {};

    // Add the main field with empty value (not the actual value from dynamicFields)
    if (selectedKbTypeConfig.key) {
      metadataObject[selectedKbTypeConfig.key] = '';
    }

    // Add empty placeholders for nested fields (they will be filled during file operations)
    const addNestedFields = (folders: any) => {
      if (!folders) return;

      const folderArray = Array.isArray(folders) ? folders : [folders];
      folderArray.forEach(folder => {
        metadataObject[folder.key] = '';

        if (folder.folders) {
          addNestedFields(folder.folders);
        }
      });
    };

    if (selectedKbTypeConfig.folders) {
      addNestedFields(selectedKbTypeConfig.folders);
    }

    const result = JSON.stringify(metadataObject);
    return result;
  };

  const handleSaveEdit = async () => {
    if (!editingKbInfo) return;

    // Validation
    if (!editingKbInfo.kbId.trim()) {
      toast.error("KB ID is mandatory");
      return;
    }

    if (!editingKbInfo.bucketName.trim()) {
      toast.error("Bucket Name is mandatory");
      return;
    }

    if (!editingKbInfo.dataSourceId.trim()) {
      toast.error("Data Source ID is mandatory");
      return;
    }

    // Validate the dynamic field (KB Type Value/Account Name/etc.) when subType=true
    const selectedKbTypeConfig = kbTypes.find(kb => kb.kbType === editingKbInfo.kbType);
    if (selectedKbTypeConfig?.subType === true && !editingKbInfo.kbTypeValue?.trim()) {
      const fieldName = (selectedKbTypeConfig as any)?.name || "KB Type Value";
      toast.error(`${fieldName} is mandatory for this KB Type`);
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate metadata based on dynamic fields
      const generatedMetadata = generateMetadataFromDynamicFields();

      // Get the kbTypeId from dynamicFieldIds using the main field key
      const mainFieldKey = selectedKbTypeConfig?.key;
      const kbTypeIdToSend = mainFieldKey ? editingKbInfo.dynamicFieldIds[mainFieldKey] : editingKbInfo.kbTypeId;

      // Use the same PUT method for both create and edit
      // For create mode, set id to null
      const dataToSend = {
        ...editingKbInfo,
        id: isCreateMode ? null : editingKbInfo.id,
        kbTypeId: kbTypeIdToSend || '',
        metadataSchema: generatedMetadata,
        key: selectedKbTypeConfig?.key || ''
      };

      await api.updateKbInfo(dataToSend);

      if (isCreateMode) {
        toast.success("Knowledge base information created successfully");
      } else {
        toast.success("Knowledge base information updated successfully");
      }

      setIsEditDialogOpen(false);
      setEditingKbInfo(null);
      setIsCreateMode(false);

      // Reload the data by calling search again
      handleSearch();
    } catch (error: any) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} KB info:`, error);
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditingKbInfo(null);
    setIsCreateMode(false);
  };

  // Navigate to file management
  const handleViewFiles = (kbInfo: KbInfoResponse) => {
    if (!kbInfo.bucketName || kbInfo.bucketName.trim() === "") {
      toast.error("No bucket name configured for this knowledge base. Please edit the record to add a bucket name.");
      return;
    }

    if (!kbInfo.id) {
      toast.error("Invalid knowledge base record. Missing ID.");
      return;
    }

    const route = getFileManagementRoute(kbInfo.id);
    navigate(route);
  };

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: kbInfoData && Array.isArray(kbInfoData) && hasSearched ? {
        count: total,
        label: 'record',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
    });

    return () => {
      setHeaderData(null);
    };
  }, [kbInfoData, total, hasSearched, setHeaderData]);

  // Re-query when pageSize changes
  useEffect(() => {
    if (currentFilterRef.current && hasSearched) {
      executeQuery(currentFilterRef.current, 1);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // Initialize form data from URL parameters but don't auto-search
  useEffect(() => {
    const agentTypeFromUrl = searchParams.get("agentType");
    const kbTypeFromUrl = searchParams.get("kbType");

    if (agentTypeFromUrl || kbTypeFromUrl) {
      setFormData(prev => ({
        ...prev,
        agentType: agentTypeFromUrl || prev.agentType,
        kbType: kbTypeFromUrl || prev.kbType,
      }));
    }
  }, []); // Empty dependency array - only run on mount

  const selectedKbTypeConfig = kbTypes.find(kb => kb.kbType === formData.kbType);

  // Helper function to check if all required dynamic fields are filled
  const areDynamicFieldsValid = () => {
    if (!editingKbInfo) return true;
    const editingKbTypeConfig = kbTypes.find(kb => kb.kbType === editingKbInfo.kbType);
    if (!editingKbTypeConfig) return true;

    // Only check if main key is required and filled
    // Skip validation if the config doesn't have a 'name' property (field is not rendered)
    if (editingKbTypeConfig.key) {
      const hasName = !!(editingKbTypeConfig.name && editingKbTypeConfig.name.trim());
      
      // If name property doesn't exist, skip validation for this field
      if (!hasName) {
        return true;
      }
      
      // If name exists, validate that the field is filled
      if (!editingKbInfo.dynamicFields[editingKbTypeConfig.key]?.trim()) {
        return false;
      }
    }

    return true;
  };
  const showCreateButton = hasSearched && (
    (Array.isArray(kbInfoData) && kbInfoData.length === 0) ||
    selectedKbTypeConfig?.subType === true
  );

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg mb-4">
            {/* Compact Header */}
            <div className="px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find knowledge base by agent type and KB type
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-4 pb-0">
              <div className="flex items-end gap-3 flex-wrap">
                {/* Agent Type */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                    Agent Type <span className="text-destructive text-base font-bold">*</span>
                  </label>
                  <Select value={formData.agentType} onValueChange={(value) => handleInputChange("agentType", value)}>
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select Agent Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGENT_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* KB Type */}
                <div className="space-y-1.5 flex-1 min-w-[150px]">
                  <label htmlFor="kbType" className="block text-sm font-semibold text-foreground">
                    KB Type <span className="text-destructive text-base font-bold">*</span>
                  </label>
                  <Select
                    value={formData.kbType}
                    onValueChange={(value) => handleInputChange("kbType", value)}
                    disabled={!formData.agentType || isLoadingConfig}
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder={
                        isLoadingConfig ? "Loading..." :
                          !formData.agentType ? "Select Agent Type first" :
                            "Select KB Type"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {kbTypes.map((kb) => (
                        <SelectItem key={kb.kbType} value={kb.kbType}>
                          {kb.kbType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={!formData.agentType || !formData.kbType || isLoading}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isLoading ? (
                      <>
                        <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && hasSearched && (
            <Card className="shadow-md border-border bg-card">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results Table */}
          {kbInfoData && hasSearched && (
            <>
              {Array.isArray(kbInfoData) && kbInfoData.length === 0 ? (
                <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Data Found</h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any knowledge base information matching your search criteria.
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          onClick={handleReset}
                          className="text-primary border-primary hover:bg-primary/10 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Clear Filters
                        </Button>
                        {showCreateButton && hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') && (
                          <Button
                            onClick={handleCreate}
                            className="bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create {formData.kbType}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="border border-border/50 bg-card backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                          <Database className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <h2 className="text-xl font-bold text-foreground">
                            Knowledge Base Information
                          </h2>
                          <Badge variant="secondary" className="text-xs font-medium">
                            {total} {total === 1 ? 'record' : 'records'}
                          </Badge>
                          {showCreateButton && hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') && (
                            <>
                              <span className="text-muted-foreground/60">•</span>
                              <Button
                                onClick={handleCreate}
                                size="sm"
                                className="h-8 px-3 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-xs rounded-md"
                              >
                                <Plus className="h-3 w-3 mr-1.5" />
                                Create {formData.kbType}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="w-full">
                        <div className="overflow-hidden rounded-lg border border-border/50">
                          <Table>
                            <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                              <TableRow>
                                <TableHead className="font-semibold">Sl No</TableHead>
                                <TableHead className="font-semibold">Agent Type</TableHead>
                                <TableHead className="font-semibold">KB Type</TableHead>
                                <TableHead className="font-semibold">KB Type Value</TableHead>
                                <TableHead className="font-semibold">KB ID</TableHead>
                                <TableHead className="font-semibold">Bucket Name</TableHead>
                                <TableHead className="font-semibold">Data Source ID</TableHead>
                                <TableHead className="font-semibold">Updated By</TableHead>
                                <TableHead className="font-semibold">Updated At</TableHead>
                                <TableHead className="font-semibold text-center">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {kbInfoData?.map((row, index) => {
                                // Calculate serial number based on current page and index
                                const serialNumber = (currentPage - 1) * pageSize + index + 1;
                                return (
                                  <TableRow
                                    key={`${row.agentType}-${row.kbType}-${row.kbId}-${index}`}
                                    className="transition-colors hover:bg-muted/50 cursor-pointer"
                                  >
                                    <TableCell className="font-medium">{serialNumber}</TableCell>
                                    <TableCell className="font-medium">{row.agentType}</TableCell>
                                    <TableCell>{row.kbType}</TableCell>
                                    <TableCell>{row.kbTypeValue || "-"}</TableCell>
                                    <TableCell className="font-mono text-sm">{row.kbId}</TableCell>
                                    <TableCell className="max-w-xs truncate" title={row.bucketName}>
                                      {row.bucketName || (
                                        <span className="text-destructive italic">Not configured</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="max-w-xs truncate" title={row.dataSourceId}>
                                      {row.dataSourceId}
                                    </TableCell>
                                    <TableCell>{row.updatedBy}</TableCell>
                                    <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-2 justify-center">
                                        {/* Only show View Files button when fileUpload is not false */}
                                        {(() => {
                                          const rowKbConfig = kbTypes.find(kb => kb.kbType === row.kbType);
                                          return rowKbConfig?.fileUpload !== false && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewFiles(row);
                                              }}
                                              title={row.bucketName ? "View Files" : "No bucket configured"}
                                              disabled={!row.bucketName || row.bucketName.trim() === ""}
                                            >
                                              <FolderOpen className="h-4 w-4" />
                                            </Button>
                                          );
                                        })()}
                                        {hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit(row);
                                            }}
                                            title="Edit"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Pagination matching dashboard style */}
                      <div className="flex items-center justify-between px-4 mt-4">
                        <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                          {total} {total === 1 ? 'record' : 'records'}
                        </div>
                        <div className="flex w-full items-center gap-8 lg:w-fit">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Rows per page</span>
                            <Select
                              value={pageSize.toString()}
                              onValueChange={(value) => {
                                const newSize = Number(value);
                                setPageSize(newSize);
                                setCurrentPage(1);
                              }}
                              disabled={isLoading}
                            >
                              <SelectTrigger className="h-8 w-[70px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                                  <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                              Page {currentPage} of {Math.ceil(total / pageSize) || 1}
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1 || isLoading}
                                title="First page"
                              >
                                <ChevronsLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1 || isLoading}
                                title="Previous page"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                                title="Next page"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCurrentPage(Math.ceil(total / pageSize))}
                                disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                                title="Last page"
                              >
                                <ChevronsRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}

          {/* Error State */}
          {error && hasSearched && (
            <Card className="shadow-md border-destructive/50 bg-card">
              <CardContent className="p-12 text-center">
                <p className="text-destructive">Error loading knowledge base information. Please try again.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreateMode ? 'Create' : 'Edit'} Knowledge Base Information
            </DialogTitle>
            <DialogDescription>
              {isCreateMode
                ? 'Configure the knowledge base settings for this agent type and KB type combination. Metadata schema will be auto-generated based on the selected KB Type.'
                : 'Update the knowledge base configuration. Metadata schema will be auto-generated based on the KB Type.'
              }
            </DialogDescription>
          </DialogHeader>

          {editingKbInfo && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Agent Type - Read Only */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Agent Type</label>
                  <input
                    type="text"
                    value={editingKbInfo.agentType}
                    className={cn(inputStyles, "bg-muted/50 cursor-not-allowed")}
                    readOnly
                  />
                </div>
                {/* KB Type - Read Only */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">KB Type</label>
                  <input
                    type="text"
                    value={editingKbInfo.kbType}
                    className={cn(inputStyles, "bg-muted/50 cursor-not-allowed")}
                    readOnly
                  />
                </div>
              </div>

              {/* Dynamic KB Type Fields */}
              {editingKbInfo && (() => {
                const editingKbTypeConfig = kbTypes.find(kb => kb.kbType === editingKbInfo.kbType);
                return editingKbTypeConfig && (
                  <div className="space-y-4">
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold mb-3">KB Type Configuration</h4>
                      <DynamicKbForm
                        kbConfig={editingKbTypeConfig}
                        agentType={editingKbInfo.agentType}
                        values={editingKbInfo.dynamicFields}
                        onChange={handleDynamicFieldChange}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 gap-4">
                {/* KB ID - Mandatory */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    KB ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingKbInfo.kbId}
                    onChange={(e) => handleEditInputChange("kbId", e.target.value)}
                    className={cn(inputStyles)}
                    placeholder="Enter KB ID"
                    required
                  />
                </div>

                {/* Bucket Name - Mandatory */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Bucket Name <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingKbInfo.bucketName}
                    onChange={(e) => handleEditInputChange("bucketName", e.target.value)}
                    className={cn(inputStyles)}
                    placeholder="Enter Bucket Name"
                    required
                  />
                </div>

                {/* Data Source ID - Mandatory */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Data Source ID <span className="text-red-500 text-base font-bold">*</span>
                  </label>
                  <textarea
                    value={editingKbInfo.dataSourceId}
                    onChange={(e) => handleEditInputChange("dataSourceId", e.target.value)}
                    className={cn(inputStyles)}
                    placeholder="Enter Data Source ID"
                    rows={3}
                    required
                  />
                </div>

                {/* Metadata Schema - Auto-generated based on dynamic fields */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Metadata Schema
                      <span className="text-xs text-gray-500 ml-2">(Auto-generated based on KB Type fields)</span>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (editingKbInfo) {
                          const metadata = generateMetadataFromDynamicFields();
                          // Force re-render by updating the editingKbInfo
                          setEditingKbInfo(prev => ({ ...prev!, metadataSchema: metadata }));
                        }
                      }}
                      className="h-7 px-2 text-xs"
                    >
                      Regenerate
                    </Button>
                  </div>
                  <textarea
                    value={(() => {
                      const metadata = generateMetadataFromDynamicFields();
                      if (!metadata) return '';
                      try {
                        return JSON.stringify(JSON.parse(metadata), null, 2);
                      } catch {
                        return metadata;
                      }
                    })()}
                    className={cn(textareaStyles, "bg-muted/50 cursor-not-allowed font-mono text-xs")}
                    placeholder="Metadata will be auto-generated based on KB Type fields"
                    rows={6}
                    readOnly
                  />
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <p><span className="text-red-500 text-base font-bold">*</span> All fields are mandatory</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSubmitting || !editingKbInfo?.kbId.trim() || !editingKbInfo?.bucketName.trim() || !editingKbInfo?.dataSourceId.trim() || !areDynamicFieldsValid()}
            >
              {isSubmitting ? (isCreateMode ? "Creating..." : "Saving...") : (isCreateMode ? "Create" : "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default KnowledgeBaseSetupPage;
